import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  Bell,
  CheckCircle,
  Clock,
  Loader2,
  MessageCircle,
  MessageSquare,
  Phone,
  Smartphone,
  XCircle,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { trpc, trpcClient } from '@/utils/trpc';

export function WhatsAppSettings() {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isLinking, setIsLinking] = useState(false);

  const whatsappStatus = useQuery(trpc.whatsapp.getStatus.queryOptions());
  const queryClient = useQueryClient();

  const linkMutation = useMutation({
    mutationFn: (data: { phoneNumber: string }) =>
      trpcClient.whatsapp.linkPhone.mutate(data),
    onSuccess: (data) => {
      toast.success('WhatsApp vinculado com sucesso!', {
        description:
          'Agora você pode enviar mensagens para gerenciar suas finanças.',
      });
      queryClient.invalidateQueries({ queryKey: ['whatsapp', 'getStatus'] });
      setPhoneNumber('');
      setIsLinking(false);
    },
    onError: (error) => {
      toast.error('Falha ao vincular WhatsApp', {
        description: error.message,
      });
      setIsLinking(false);
    },
  });

  const unlinkMutation = useMutation({
    mutationFn: () => trpcClient.whatsapp.unlinkPhone.mutate(),
    onSuccess: () => {
      toast.success('WhatsApp desvinculado com sucesso');
      queryClient.invalidateQueries({ queryKey: ['whatsapp', 'getStatus'] });
    },
    onError: (error) => {
      toast.error('Falha ao desvincular WhatsApp', {
        description: error.message,
      });
    },
  });

  const testMutation = useMutation({
    mutationFn: (data: { message: string }) =>
      trpcClient.whatsapp.testSend.mutate(data),
    onSuccess: () => {
      toast.success('Mensagem de teste enviada!', {
        description: 'Verifique seu WhatsApp.',
      });
    },
    onError: (error) => {
      toast.error('Falha ao enviar mensagem de teste', {
        description: error.message,
      });
    },
  });

  const forceDailyNotificationMutation = useMutation({
    mutationFn: () => trpcClient.whatsapp.forceAllDailyNotifications.mutate(),
    onSuccess: () => {
      toast.success('Notificações diárias enviadas!', {
        description: 'Todos os usuários habilitados receberão a notificação.',
      });
    },
    onError: (error) => {
      toast.error('Falha ao enviar notificações', {
        description: error.message,
      });
    },
  });

  const handleLinkPhone = async () => {
    if (!phoneNumber) {
      toast.error('Por favor, insira um número de telefone');
      return;
    }

    setIsLinking(true);
    linkMutation.mutate({ phoneNumber });
  };

  const handleUnlink = async () => {
    unlinkMutation.mutate();
  };

  const handleTestMessage = () => {
    testMutation.mutate({ message: '/help' });
  };

  const formatPhoneNumber = (phone: string) => {
    // Remove whatsapp: prefix and clean
    const cleaned = phone.replace('whatsapp:', '').replace(/\D/g, '');
    // Format for Brazilian numbers
    if (cleaned.startsWith('55')) {
      const match = cleaned.match(/^(55)(\d{2})(\d{5})(\d{4})$/);
      if (match) {
        return `+${match[1]} (${match[2]}) ${match[3]}-${match[4]}`;
      }
    }
    return phone;
  };

  const status = whatsappStatus.data;

  if (whatsappStatus.isPending) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Connection Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5" />
            Conexão WhatsApp
          </CardTitle>
          <CardDescription>
            Conecte seu número do WhatsApp para registrar despesas e obter
            insights financeiros via chat.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {status?.linked ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between rounded-lg border border-green-500/20 bg-green-500/10 p-4">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                  <div>
                    <p className="font-medium text-green-700 dark:text-green-300">
                      Conectado
                    </p>
                    <p className="text-green-600 text-sm dark:text-green-400">
                      {formatPhoneNumber(status.phoneNumber || '')}
                    </p>
                  </div>
                </div>
                <Badge
                  className="bg-green-500/20 text-green-700 dark:text-green-300"
                  variant="default"
                >
                  Ativo
                </Badge>
              </div>

              {status.lastMessageAt && (
                <div className="flex items-center text-muted-foreground text-sm">
                  <Clock className="mr-2 h-4 w-4" />
                  Última mensagem:{' '}
                  {new Date(status.lastMessageAt).toLocaleString('pt-BR')}
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                <Button
                  disabled={testMutation.isPending}
                  onClick={handleTestMessage}
                  variant="outline"
                >
                  {testMutation.isPending && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Enviar Mensagem de Teste
                </Button>
                <Button
                  disabled={forceDailyNotificationMutation.isPending}
                  onClick={() => forceDailyNotificationMutation.mutate()}
                  variant="outline"
                >
                  {forceDailyNotificationMutation.isPending && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  <Bell className="mr-2 h-4 w-4" />
                  Forçar Notificação Diária
                </Button>
                <Button
                  disabled={unlinkMutation.isPending}
                  onClick={handleUnlink}
                  variant="outline"
                >
                  {unlinkMutation.isPending && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Desvincular
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between rounded-lg border border-yellow-500/20 bg-yellow-500/10 p-4">
                <div className="flex items-center space-x-3">
                  <XCircle className="h-6 w-6 text-yellow-600" />
                  <div>
                    <p className="font-medium text-yellow-900">Não Conectado</p>
                    <p className="text-sm text-yellow-700">
                      Vincule seu número do WhatsApp para começar
                    </p>
                  </div>
                </div>
                <Badge variant="secondary">Inativo</Badge>
              </div>

              <div className="space-y-3">
                <Label htmlFor="phone">Número do WhatsApp</Label>
                <div className="flex space-x-2">
                  <Input
                    className="flex-1"
                    id="phone"
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="11999999999"
                    type="tel"
                    value={phoneNumber}
                  />
                  <Button
                    disabled={isLinking || linkMutation.isPending}
                    onClick={handleLinkPhone}
                  >
                    {(isLinking || linkMutation.isPending) && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    <Phone className="mr-2 h-4 w-4" />
                    Vincular WhatsApp
                  </Button>
                </div>
                <p className="text-muted-foreground text-sm">
                  Digite apenas números (DDD + número). Ex: 11999999999
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* How to Use */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Smartphone className="h-5 w-5" />
            Como Usar a Integração WhatsApp
          </CardTitle>
          <CardDescription>
            Aprenda a gerenciar suas finanças através de mensagens no WhatsApp
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-3">
              <h4 className="font-semibold">Registrar Transações</h4>
              <div className="space-y-2 text-sm">
                <div className="flex items-center space-x-2">
                  <MessageSquare className="h-4 w-4 text-muted-foreground" />
                  <span>"comprei 50 reais de café"</span>
                </div>
                <div className="flex items-center space-x-2">
                  <MessageSquare className="h-4 w-4 text-muted-foreground" />
                  <span>"recebi 5000 de salário"</span>
                </div>
                <div className="flex items-center space-x-2">
                  <MessageSquare className="h-4 w-4 text-muted-foreground" />
                  <span>"gastei 25 com uber"</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Phone className="h-4 w-4 text-blue-600" />
                  <span className="font-medium text-blue-700">
                    🎤 Mensagens de voz também funcionam!
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <h4 className="font-semibold">Perguntas</h4>
              <div className="space-y-2 text-sm">
                <div className="flex items-center space-x-2">
                  <MessageSquare className="h-4 w-4 text-muted-foreground" />
                  <span>"quanto gastei este mês?"</span>
                </div>
                <div className="flex items-center space-x-2">
                  <MessageSquare className="h-4 w-4 text-muted-foreground" />
                  <span>"como está meu orçamento?"</span>
                </div>
                <div className="flex items-center space-x-2">
                  <MessageSquare className="h-4 w-4 text-muted-foreground" />
                  <span>"posso comprar um notebook de 2000?"</span>
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-blue-500/20 bg-blue-500/10 p-4">
            <div className="flex items-start space-x-2">
              <AlertCircle className="mt-0.5 h-5 w-5 text-blue-600" />
              <div className="text-sm">
                <p className="mb-1 font-medium text-blue-900">Começando</p>
                <p className="text-blue-800">
                  Após conectar, envie qualquer mensagem para começar. A IA
                  entende português naturalmente!
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Twilio Setup Instructions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            Instruções de Configuração
          </CardTitle>
          <CardDescription>
            Para desenvolvedores: Como configurar a integração WhatsApp
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-lg border bg-muted p-4">
            <h4 className="mb-2 font-semibold">
              Variáveis de Ambiente Necessárias
            </h4>
            <div className="space-y-1 rounded bg-muted/80 p-3 font-mono text-sm">
              <div>TWILIO_ACCOUNT_SID=your_account_sid</div>
              <div>TWILIO_AUTH_TOKEN=your_auth_token</div>
              <div>TWILIO_WHATSAPP_NUMBER=whatsapp:+14155238886</div>
              <div>
                OPENAI_API_KEY=your_openai_key # Para transcrição de voz
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-blue-500/20 bg-blue-500/10 p-4">
            <h4 className="mb-2 flex items-center gap-2 font-semibold">
              <Phone className="h-4 w-4" />
              Mensagens de Voz
            </h4>
            <p className="text-blue-800 text-sm">
              O sistema agora suporta mensagens de voz! Elas são automaticamente
              transcritas usando OpenAI Whisper e processadas pela IA como
              mensagens de texto normais.
            </p>
          </div>

          <div className="rounded-lg border bg-muted p-4">
            <h4 className="mb-2 font-semibold">URL do Webhook</h4>
            <p className="mb-2 text-muted-foreground text-sm">
              Configure esta URL no seu Console Twilio:
            </p>
            <div className="rounded bg-muted/80 p-3 font-mono text-sm">
              {process.env.NODE_ENV === 'production'
                ? 'https://your-domain.com/webhook/whatsapp'
                : 'https://your-ngrok-url.ngrok.io/webhook/whatsapp'}
            </div>
          </div>

          <p className="text-muted-foreground text-sm">
            Veja WHATSAPP_SETUP.md na raiz do projeto para instruções completas
            de configuração.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
